import * as uuid from "uuid";
import type {
  RaceSelection,
  SemSelection,
  SemSelectionLeg,
} from "@/features/betslip/types";
import {
  isRacingEvent,
  type SemToggleInput,
} from "@/features/betslip/builders/types";

const semDefaults = {
  kind: "sem",
  marketId: "",
  marketType: "",
  isSP: false,
  isMultiable: true,
  independentPerEvent: false,
  blockPromotionsBets: false,
  hasPromotionalMarkets: false,
  hasLiveMarkets: false,
  marketStatus: undefined,
} as const;

const buildCompetitors = (
  items: {
    outcomeId: string;
    competitor?: SemSelection["competitors"][string];
  }[],
): SemSelection["competitors"] => {
  const competitors: SemSelection["competitors"] = {};
  for (const item of items) {
    if (item.competitor) competitors[item.outcomeId] = item.competitor;
  }
  return competitors;
};

export const buildSemSelection = (input: SemToggleInput): SemSelection => {
  const {
    event,
    hub,
    outcomeId,
    odds,
    subSelections,
    originType,
    originId,
    mysteryMulti,
  } = input;

  const legs: SemSelectionLeg[] = subSelections.map((sub) => ({
    outcomeId: sub.outcomeId,
    marketId: sub.marketId,
    name: sub.name,
    marketName: sub.marketName ?? "",
    outcomeType: sub.outcomeType,
    odds: sub.odds,
    marketType: sub.marketType,
    competitorId: sub.competitorId,
    outcomeGroup: sub.outcomeGroup,
  }));

  const racingEvent = isRacingEvent(event) ? event : null;
  const sportEvent = isRacingEvent(event) ? null : event;

  return {
    ...semDefaults,
    outcomeId,
    eventId: event.id,
    eventName: event.eventName ?? "",
    sport: event.sport ?? "",
    startTime: event.scheduledStartTime ?? new Date(),
    hub,
    odds,
    filters: event.filters ?? [],
    originType: originType ?? "BETTING",
    originId,
    legs,
    competitors: buildCompetitors(subSelections),
    eventStatus: event.status,
    mysteryMulti,
    streamUri: sportEvent?.streamUri ?? undefined,
    eventUrl: sportEvent
      ? `${input.hub === "sports" ? "/sports" : ""}/betting/match/${sportEvent.eventId}`
      : undefined,
    seasonId: sportEvent?.seasonId,
    tournamentId: sportEvent?.tournamentId,
    silksUrl: racingEvent?.silksUrl,
    expectedResults: racingEvent?.expectedResults,
    region: racingEvent?.region,
    meetingId: racingEvent?.meetingId,
  };
};

export const buildSrmFromSingles = (
  legs: RaceSelection[],
  odds: number,
): SemSelection => {
  const base = legs[0];

  const semLegs: SemSelectionLeg[] = legs.map((leg) => ({
    outcomeId: leg.outcomeId,
    marketId: leg.marketId,
    name: leg.selectionName,
    marketName: leg.marketName,
    odds: leg.odds,
    marketType: leg.marketType,
  }));

  return {
    ...semDefaults,
    outcomeId: uuid.v4(),
    eventId: base.eventId,
    eventName: base.eventName,
    sport: base.sport,
    startTime: base.startTime,
    hub: "racing",
    odds,
    filters: base.filters,
    originType: base.originType,
    legs: semLegs,
    competitors: buildCompetitors(legs),
    eventStatus: base.eventStatus,
    silksUrl: base.silksUrl,
    region: base.region,
    expectedResults: base.expectedResults,
    meetingId: base.meetingId,
  };
};
